import restClient from "./restClient";
import {
  StandardVM,
  TestSessionsListVM,
  TestSessionVM,
  InitVM,
  UpdateStep1Request,
  ProfileOptionSelection,
  TestCaseDto,
  FunctionOptionSelection,
  Step2VM,
  UserDto,
  RoleDto,
  TestConfigurationDto,
  TestFileDto,
  ValimateExecutionSummaryVM,
  SessionSummaryVM,
  SubscriptionLevel,
  MaintenanceModeOptions,
} from "../autogenerated/models";

export interface Init {
  user: User;
}

export interface User {
  id: string;
  veracityId: string;
  name: string;
  email: string;
}
export const fetchInit = () => restClient.get<InitVM>("/api/init");

export const createTestSession = (): Promise<string> =>
  restClient.post("/api/testSessions/create");
export const fetchTestSessions = (): Promise<TestSessionsListVM> =>
  restClient.get("/api/testSessions/GetSessionsList/");
export const fetchTestSession = (sessionId: string) =>
  restClient.get<TestSessionVM>("/api/testSessions/GetSession/" + sessionId);

export const fetchStandard = (standardId: string) =>
  restClient.get<StandardVM>("/api/standards/GetStandard/" + standardId);
export const fetchConfigurations = (standardId: string) =>
  restClient.get<TestConfigurationDto[]>(
    "/api/standards/GetConfigurations/" + standardId
  );

export const fetchConfigurationsForStandard = (standardName: string) =>
  restClient.get<TestConfigurationDto[]>(
    "/api/standards/GetConfigurationsByName/" + standardName
  );

// Admin
export const fetchSubscribers = (pageNumber: number) =>
  restClient.get<UserDto[]>("/api/Admin/GetSubscribers/" + pageNumber);
export const fetchSubscribersWithSessions = (pageNumber: number) =>
  restClient.get<UserDto[]>(
    "/api/Admin/GetSubscribersWithSessions/" + pageNumber
  );
export const fetchRoles = () =>
  restClient.get<RoleDto[]>("/api/Admin/GetRoles");
export const toggleRoleForUser = (
  userId: string,
  roleId: string
): Promise<UserDto> =>
  restClient.post("/api/Admin/ToggleRole", {
    userId,
    roleId,
  });
export const setSubscriptionLevel = (
  veracityId: string,
  subscription: SubscriptionLevel
): Promise<UserDto> =>
  restClient.post("/api/Admin/SetSubscriptionLevel", {
    veracityId,
    subscription,
  });
export const setUnshacledSubscriptionLevel = (
  veracityId: string,
  subscription: SubscriptionLevel
): Promise<UserDto> =>
  restClient.post("/api/Admin/SetUnshacledSubscription", {
    veracityId,
    subscription,
  });
export const fetchReqRes = (sessionId: string) =>
  restClient.get<any>(`/api/Admin/GetReqResInfo/${sessionId}`);
export const setMaintenanceMode = (options: MaintenanceModeOptions) =>
  restClient.post<MaintenanceModeOptions>(`/api/Admin/SetMaintenanceMode`, options);

export const uploadTestFile = (
  fileDto: TestFileDto,
  file: File
): Promise<TestFileDto> => {
  var formData = new FormData();
  formData.append("testFile", file);
  formData.append("name", fileDto.name ?? "");
  formData.append("testStepId", fileDto.testStepId ?? "");
  formData.append("testDatasetId", fileDto.testDatasetId ?? "");
  formData.append("testConfigurationId", fileDto.testConfigurationId ?? "");
  formData.append("testSessionId", fileDto.testSessionId ?? "");
  formData.append(
    "internalEvaluation",
    fileDto.internalEvaluation ? "true" : "false"
  );
  formData.append(
    "contentFileNames",
    fileDto.contentFileNames?.join("|") ?? ""
  );

  return restClient.post("/api/TestFiles/Upload", formData);
};

export const addConfiguration = (
  configurationName: string,
  dependencyId: string,
  fileName: string,
  file: File | undefined
): Promise<Array<TestConfigurationDto>> => {
  var formData = new FormData();
  formData.append("configurationName", configurationName);
  formData.append("dependencyId", dependencyId);
  formData.append("fileName", fileName);
  formData.append("configurationFile", file ?? "");

  return restClient.post("/api/Admin/AddConfiguration", formData);
};

export const deleteTestFile = (fileId: string) =>
  restClient.remove<any>("/api/testFiles/Delete/" + fileId);

export const deleteTestFiles = (files: (TestFileDto | undefined)[]) =>
  restClient.post<any>("/api/testFiles/DeleteFiles", { files });

// setup wizard
export const updateStep1 = (data: UpdateStep1Request) =>
  restClient.put<any>("/api/setupwizard/step1", data);

export const getRequiredTestUseCases = (
  profileOptions: ProfileOptionSelection[],
  functionOptions: FunctionOptionSelection[]
): Promise<TestCaseDto[]> =>
  restClient.post("/api/testSelection/GetRequiredTestUseCases", {
    selectedFunctionOptions: functionOptions,
    selectedProfileOptions: profileOptions,
  });

export const updateStep2 = (
  sessionId: string,
  profileOptions: ProfileOptionSelection[],
  functionOptions: FunctionOptionSelection[]
): Promise<void> =>
  restClient.post("/api/testSelection/SaveTestCaseSelection", {
    sessionId,
    selectedFunctionOptions: functionOptions,
    selectedProfileOptions: profileOptions,
  });

export const fetchTestCasesForSession = (sessionId: string) =>
  restClient.get<TestCaseDto[]>(
    "/api/TestSelection/GetTestCasesForSession/" + sessionId
  );

export const fetchSessionStep2Selections = (sessionId: string) =>
  restClient.get<Step2VM>(
    "/api/TestSelection/GetSessionStep2Selections/" + sessionId
  );

export const deleteSession = (sessionId: string) =>
  restClient.remove<any>("/api/testSessions/" + sessionId);
export const cloneSession = (sessionId: string, withFiles: boolean) =>
  restClient.put<string>(`/api/testSessions/clone/${sessionId}/${withFiles}`);

export const requireAssist = (sessionId: string) =>
  restClient.get<SessionSummaryVM>(
    "/api/testSessions/RequireAssist/" + sessionId
  );

export const approveSession = (sessionId: string) =>
  restClient.get<SessionSummaryVM>("/api/testSessions/Approve/" + sessionId);

export const sendSession = (sessionId: string, message: string) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/Send", {
    sessionId,
    message,
  });

export const setSessionStatus = (
  sessionId: string,
  statusShortName: string,
  message: string
) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/SetSessionStatus", {
    sessionId,
    statusShortName,
    message,
  });

export const failCertificationOfSession = (
  sessionId: string,
  message: string
) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/FailCertification", {
    sessionId,
    message,
  });

export const passCertificationOfSession = (
  sessionId: string,
  message: string
) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/PassCertification", {
    sessionId,
    message,
  });

export const failReviewOfSession = (sessionId: string, message: string) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/FailReview", {
    sessionId,
    message,
  });

export const passReviewOfSession = (sessionId: string, message: string) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/PassReview", {
    sessionId,
    message,
  });

export const failVerificationOfSession = (sessionId: string, message: string) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/FailVerification", {
    sessionId,
    message,
  });

export const passVerificationOfSession = (sessionId: string, message: string) =>
  restClient.post<SessionSummaryVM>("/api/testSessions/PassVerification", {
    sessionId,
    message,
  });

export const executeTestSession = (
  sessionId: string
): Promise<ValimateExecutionSummaryVM> =>
  restClient.post("/api/Valimate/ExecuteTestSession", { sessionId });

export const rerunTestCase = (
  sessionId: string,
  testCaseId: string,
  configurationId: string
): Promise<ValimateExecutionSummaryVM> =>
  restClient.post("/api/Valimate/RerunTestCase", {
    sessionId,
    testCaseId,
    configurationId,
  });

export const getSessionRunResults = (
  sessionId: string
): Promise<ValimateExecutionSummaryVM> =>
  restClient.get("/api/Valimate/GetSessionRunResults/" + sessionId);

export const fetchTestSessionSummary = (sessionId: string) =>
  restClient.get<SessionSummaryVM>(
    "/api/testSessions/GetSessionSummary/" + sessionId
  );

export const DowloadSessionFilesForInspection = (sessionId: string) =>
  restClient.get<Blob>(
    "/api/TestFiles/DowloadSessionFilesForInspection/" + sessionId,
    { responseType: "blob" }
  );

export const DowloadExecutionStepFiles = (
  sessionId: string,
  stepId: string,
  configurationId: string
) =>
  restClient.post<Blob>(
    "/api/TestFiles/DowloadExecutionStepFiles",
    {
      testSessionId: sessionId,
      testStepId: stepId,
      testConfigurationId: configurationId,
    },
    { responseType: "blob" }
  );

export const DowloadExecutionReport = (runId: string) =>
  restClient.get<Blob>("/api/TestFiles/DownloadExecutionReport/" + runId, {
    responseType: "blob",
  });

export const DownloadExecutionSummaryReport = (runId: string) =>
  restClient.get<Blob>(
    "/api/TestFiles/DownloadExecutionSummaryReport/" + runId,
    {
      responseType: "blob",
    }
  );

export const DownloadAttestationOfConformity = (sessionId: string) =>
  restClient.get<Blob>(
    "/api/TestFiles/DownloadAttestationOfConformity/" + sessionId,
    {
      responseType: "blob",
    }
  );

export const DownloadReviewOutcome = (sessionId: string) =>
  restClient.get<Blob>("/api/TestFiles/DownloadReviewOutcome/" + sessionId, {
    responseType: "blob",
  });

export const DowloadTestMaterials = (testCaseIds: Array<string>) =>
  restClient.get<Blob>(`/api/files/materials?ids=${testCaseIds.join(";")}`, {
    responseType: "blob",
  });

export const DowloadTestSessionMaterials = (sessionId: string) =>
  restClient.get<Blob>(`/api/files/sessionMaterials/${sessionId}`, {
    responseType: "blob",
  });

export const DownloadTemplate = () =>
  restClient.get<Blob>("/api/DockerProcessing/template/download", {
    responseType: "blob",
  });

// Docker Processing APIs
export const GenerateNonContextDriven = (formData: FormData) =>
  restClient.post("/api/DockerProcessing/generateNcd", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const ExportCDTemplate = (formData: FormData) =>
  restClient.post("/api/DockerProcessing/exportCdTemplate", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const ValidateRules = (formData: FormData) =>
  restClient.post("/api/DockerProcessing/validate", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const GenerateContextDriven = (formData: FormData) =>
  restClient.post("/api/DockerProcessing/generateContextDriven", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

// Async Docker Processing APIs
export const StartProcessing = (formData: FormData, mode: string) =>
  restClient.post(`/api/DockerProcessing/start?mode=${mode}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export const GetProcessingStatus = (executionId: string) =>
  restClient.get(`/api/DockerProcessing/status/${executionId}`);

export const fetchLastRunInputFiles = (testSessionId: string) =>
  restClient.get<{ sessionIdentifier: string; files: { fileName: string; kind: string }[] }>(
    `/api/DockerProcessing/test-sessions/${testSessionId}/last-run-inputs`
  );

export const downloadLastRunInputFile = async (testSessionId: string, fileName: string): Promise<ArrayBuffer> => {
  const axios = (await import('axios')).default;
  const response = await axios.get(
    `/api/DockerProcessing/test-sessions/${testSessionId}/download-input/${encodeURIComponent(fileName)}`,
    { responseType: 'arraybuffer' }
  );
  return response.data;
};

export const downloadSessionOutputFile = async (sessionIdentifier: string, fileName: string): Promise<ArrayBuffer> => {
  const axios = (await import('axios')).default;
  const response = await axios.get(
    `/api/dockerprocessing/sessions/${sessionIdentifier}/download/${encodeURIComponent(fileName)}`,
    { responseType: 'arraybuffer' }
  );
  return response.data;
};

// unSHACLed Sessions
export const fetchCustomProfileSessions = () =>
  restClient.get<any[]>("/api/Unshacled/sessions");

export const fetchCustomProfileSessionRuns = (sessionId: string) =>
  restClient.get<any[]>(`/api/Unshacled/sessions/${sessionId}/runs`);

export const fetchSessionInputFileNames = (sessionIdentifier: string) =>
  restClient.get<string[]>(`/api/DockerProcessing/sessions/${sessionIdentifier}/inputs`);

export const createCustomProfileSession = (name: string) =>
  restClient.post<any>("/api/Unshacled/sessions", { name });

export const deleteCustomProfileSession = (id: string) =>
  restClient.remove<any>(`/api/Unshacled/sessions/${id}`);

export const duplicateCustomProfileSession = (id: string) =>
  restClient.post<any>(`/api/Unshacled/sessions/${id}/duplicate`, {});
